// components/FastenerConfigDialog.js

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DEFAULT_PIPE_COMBOS } from './pipeCatalog';

/**
 * Извлекаем уникальные диаметры из каталога труб (до 159 включительно)
 * + добавляем специальные диаметры для крепежей
 */
const getAvailableDiameters = () => {
  const diameters = new Set();
  Object.keys(DEFAULT_PIPE_COMBOS).forEach(key => {
    const [diameter] = key.split('|');
    diameters.add(parseFloat(diameter));
  });
  
  // Добавляем специальные диаметры для крепежей
  diameters.add(21.3);
  diameters.add(26.9);
  diameters.add(33.7);
  
  // Исключаем диаметры: все до 38 включительно (кроме добавленных вручную), 54, 306
  const excludedDiameters = [54, 306];
  const manuallyAdded = [21.3, 26.9, 33.7];
  
  return Array.from(diameters)
    .filter(d => (d > 38 || manuallyAdded.includes(d)) && d <= 159 && !excludedDiameters.includes(d)) // До 159 включительно
    .sort((a, b) => a - b);
};

const availableDiameters = getAvailableDiameters();

/**
 * Диалоговое окно для настройки параметров крепежа перед добавлением на канвас
 * @param {boolean} open - Открыт ли диалог
 * @param {function} onClose - Функция закрытия без добавления
 * @param {function} onConfirm - Функция подтверждения с параметрами (clampDiameter, axialDistance)
 * @param {object} fastenerData - Данные о крепеже из fastenersData
 */
function FastenerConfigDialog({ open, onClose, onConfirm, fastenerData }) {
  // Состояния для параметров (сохраняются между открытиями)
  const [clampDiameter, setClampDiameter] = useState(76.1); // Диаметр хомутов (для настенных)
  const [axialDistance, setAxialDistance] = useState(200); // Межосевое расстояние (для настенных)
  const [overhang, setOverhang] = useState(150); // Вылет от оси трубы до стены
  const [standardOverhang, setStandardOverhang] = useState(true);
  const [height, setHeight] = useState(400); // Высота (для напольных)
  const [rangeOffset, setRangeOffset] = useState('0'); // Сдвиг диапазона GS-03 / GS-04, без знака
  const [rangeDirection, setRangeDirection] = useState('up');
  const [standardRange, setStandardRange] = useState(true);
  const signedRangeOffset = (() => {
    if (standardRange) return 0;
    const amount = Math.abs(parseFloat(rangeOffset) || 0);
    return rangeDirection === 'down' ? -amount : amount;
  })();
  const [pipeDiameter, setPipeDiameter] = useState(323.9); // Диаметр трубы (для крепежа больших труб)
  const [errorMessage, setErrorMessage] = useState(''); // Сообщение об ошибке

  // Определяем, является ли крепеж напольным
  const isFloorFastener = fastenerData?.isFloorFastener || false;
  // Определяем, является ли крепеж для больших труб
  const isLargePipeFastener = fastenerData?.isLargePipeFastener || false;

  // Устанавливаем первый доступный диаметр при открытии диалога для крепежа больших труб
  React.useEffect(() => {
    if (open && isLargePipeFastener && fastenerData?.supportedDiameters) {
      const supportedDiameters = fastenerData.supportedDiameters;
      // Проверяем, есть ли текущий диаметр в списке поддерживаемых
      if (!supportedDiameters.includes(pipeDiameter)) {
        // Устанавливаем первый доступный диаметр
        setPipeDiameter(supportedDiameters[0]);
      }
    }
  }, [open, isLargePipeFastener, fastenerData, pipeDiameter]);

  // НЕ сбрасываем значения при открытии - они сохраняются между добавлениями крепежей

  // Вычисляем минимальное межосевое расстояние для настенных крепежей
  const getMinAxialDistance = (diameter, fastenerKey) => {
    // Настенные крепежи
    if (fastenerKey === 'YKRS-01') {
      return diameter + 60; // YKRS-01: диаметр + 60 мм
    } else if (fastenerKey === 'YKRS-02') {
      return Math.max(150, diameter + 70); // YKRS-02: не меньше 150 мм при любом хомуте
    }
    // Fallback для других настенных крепежей
    return diameter >= 76.1 ? 150 : 100;
  };

  // Округляем межосевое расстояние до ближайшего кратного 5 вверх
  const roundUpToNearest5 = (value) => {
    return Math.ceil(value / 5) * 5;
  };

  // Ближайшая высота сетки шага: 400, 450, 500… от текущего минимума диапазона
  const snapToHeightStep = (value, minHeight, maxHeight, heightStep) => {
    const n = Number(value);
    if (!heightStep || !Number.isFinite(n)) return n;
    const snapped = minHeight + Math.round((n - minHeight) / heightStep) * heightStep;
    return Math.min(maxHeight, Math.max(minHeight, snapped));
  };

  const handleConfirm = () => {
    const fastenerKey = fastenerData?.displayName || 'YKRS-01'; // Получаем ключ крепежа
    
    // Валидация для напольных крепежей
    if (isFloorFastener) {
      const noMufta = fastenerData?.noMufta || false; // Проверяем, нужна ли муфта
      const muftaHeight = noMufta ? 0 : 22; // Высота муфты (0 если не нужна)
      const offset = fastenerData?.lowerPipeLength ? signedRangeOffset : 0;
      const lowerPipe = Number(fastenerData?.lowerPipeLength) || 0;
      const floorClearance = Number(fastenerData?.pipeFloorClearance) || 0;
      const raiseMax = Number(fastenerData?.rangeRaiseMax) || 0;
      if (lowerPipe && floorClearance && offset < -floorClearance) {
        setErrorMessage(`Уменьшить можно не больше чем на ${floorClearance} мм: труба 1 1/4" упрётся в пол`);
        return;
      }
      if (lowerPipe && raiseMax && offset > raiseMax) {
        setErrorMessage(`Увеличить можно не больше чем на ${raiseMax} мм`);
        return;
      }
      if (lowerPipe && lowerPipe + offset < 40) {
        setErrorMessage('Нижняя труба не может быть короче 40 мм');
        return;
      }
      const minHeight = (fastenerData?.minHeight || 200) + offset;
      const maxHeight = (fastenerData?.maxHeight || 600) + offset;
      const heightStep = fastenerData?.heightStep; // Шаг изменения высоты (если задан)
      
      let heightValue = Number(height);
      if (!(heightValue > 0)) {
        setErrorMessage('Высота должна быть больше нуля');
        return;
      }

      if (heightStep) {
        heightValue = snapToHeightStep(heightValue, minHeight, maxHeight, heightStep);
        setHeight(heightValue);
      } else if (heightValue < minHeight || heightValue > maxHeight) {
        setErrorMessage(`Высота должна быть в диапазоне ${minHeight}-${maxHeight} мм`);
        return;
      }
      
      // Вычисляем высоту крепежа (без муфты, если она есть)
      const fastenerOnlyHeight = heightValue - muftaHeight;
      
      // Очищаем ошибку и подтверждаем для напольного крепежа
      setErrorMessage('');
      onConfirm({
        height: fastenerOnlyHeight, // Высота только крепежа (без муфты)
        rangeOffset: offset,
        isFloorFastener: true,
        addTopMufta: !noMufta, // Муфта добавляется только если не установлен флаг noMufta
        pipeDiameter: isLargePipeFastener ? parseFloat(pipeDiameter) : undefined, // Диаметр трубы для крепежа больших труб
      });
      return;
    }
    
    // Валидация для настенных крепежей
    const minAxialDistanceRaw = getMinAxialDistance(clampDiameter, fastenerKey);
    const minAxialDistance = roundUpToNearest5(minAxialDistanceRaw); // Округляем вверх до кратного 5
    
    const hasAxial = fastenerData?.hasAxialDistance !== false;
    if (clampDiameter <= 0 || (hasAxial && axialDistance <= 0)) {
      setErrorMessage('Значения должны быть больше нуля');
      return;
    }

    if (hasAxial && axialDistance < minAxialDistance) {
      setErrorMessage(`Для хомута ⌀${clampDiameter} мм минимальное межосевое расстояние: ${minAxialDistance} мм`);
      return;
    }

    const overhangRange = fastenerData?.overhangRange;
    const standardValue = overhangRange?.standard ?? 150;
    const overhangValue = standardOverhang ? standardValue : parseFloat(overhang);
    if (overhangRange) {
      if (!(overhangValue >= overhangRange.min && overhangValue <= overhangRange.max)) {
        setErrorMessage(`Вылет должен быть в диапазоне ${overhangRange.min}–${overhangRange.max} мм`);
        return;
      }
    }

    // Очищаем ошибку и подтверждаем для настенного крепежа
    setErrorMessage('');
    onConfirm({
      clampDiameter: parseFloat(clampDiameter),
      axialDistance: hasAxial ? parseFloat(axialDistance) : undefined,
      overhang: overhangRange ? overhangValue : 150,
      isFloorFastener: false,
    });
  };

  const handleClose = () => {
    setErrorMessage(''); // Очищаем ошибку при закрытии
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Настройка крепежа: {fastenerData?.displayName || 'Крепеж'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {/* Сообщение об ошибке */}
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}
          
          {/* Поля для НАПОЛЬНЫХ крепежей */}
          {isFloorFastener ? (
            <>
              {(() => {
                const offset = fastenerData?.lowerPipeLength ? signedRangeOffset : 0;
                const minH = (fastenerData?.minHeight || 200) + offset;
                const maxH = (fastenerData?.maxHeight || 600) + offset;
                return (
                  <Box>
                    <Typography sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}>
                      Диапазон высоты: {minH}–{maxH} мм
                    </Typography>
                    {fastenerData?.heightStep ? (
                      <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.3, mt: 0.25, color: 'text.primary' }}>
                        Шаг {fastenerData.heightStep} мм
                      </Typography>
                    ) : null}
                    <TextField
                      label="Высота предпросмотра, мм"
                      type="number"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      onBlur={() => {
                        if (!fastenerData?.heightStep) return;
                        setHeight(snapToHeightStep(height, minH, maxH, fastenerData.heightStep));
                      }}
                      fullWidth
                      sx={{ mt: 1.5 }}
                      inputProps={{
                        step: fastenerData?.heightStep || 1,
                        min: minH,
                        max: maxH,
                      }}
                      helperText="Эта высота видна на главном виде и в модели"
                    />
                  </Box>
                );
              })()}

              {/* Для крепежа больших труб - выбор диаметра трубы */}
              {isLargePipeFastener && (
                <FormControl fullWidth>
                  <InputLabel>Диаметр трубы</InputLabel>
                  <Select
                    value={pipeDiameter}
                    label="Диаметр трубы"
                    onChange={(e) => setPipeDiameter(e.target.value)}
                  >
                    {(fastenerData?.supportedDiameters || [273, 325, 377, 426, 478, 530, 609.6]).map((diameter) => (
                      <MenuItem key={diameter} value={diameter}>
                        ⌀{diameter} мм
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {fastenerData?.lowerPipeLength ? (
                <>
                  <FormControlLabel
                    sx={{ m: 0, alignItems: 'center' }}
                    control={(
                      <Checkbox
                        size="small"
                        checked={standardRange}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setStandardRange(checked);
                          if (checked) setRangeOffset('0');
                        }}
                      />
                    )}
                    label="Стандартный диапазон"
                  />
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={rangeDirection}
                      disabled={standardRange}
                      onChange={(event, value) => {
                        if (!value) return;
                        setRangeDirection(value);
                        const cap = value === 'down'
                          ? (fastenerData.pipeFloorClearance || 85)
                          : (fastenerData.rangeRaiseMax || 545);
                        const amount = Math.abs(parseFloat(rangeOffset) || 0);
                        if (amount > cap) setRangeOffset(String(cap));
                      }}
                    >
                      <ToggleButton value="up">Увеличить</ToggleButton>
                      <ToggleButton value="down">Уменьшить</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                      label="На сколько, мм"
                      type="number"
                      value={standardRange ? '0' : rangeOffset}
                      onChange={(e) => {
                        const cap = rangeDirection === 'down'
                          ? (fastenerData.pipeFloorClearance || 85)
                          : (fastenerData.rangeRaiseMax || 545);
                        const amount = Math.abs(parseFloat(String(e.target.value).replace('-', '')) || 0);
                        setRangeOffset(String(Math.min(amount, cap)));
                      }}
                      disabled={standardRange}
                      sx={{ flex: 1 }}
                      inputProps={{
                        step: 1,
                        min: 0,
                        max: rangeDirection === 'down'
                          ? (fastenerData.pipeFloorClearance || 85)
                          : (fastenerData.rangeRaiseMax || 545),
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Уменьшить до {fastenerData.pipeFloorClearance || 85} мм, увеличить до {fastenerData.rangeRaiseMax || 545} мм
                  </Typography>
                </>
              ) : null}
            </>
          ) : (
            /* Поля для НАСТЕННЫХ крепежей */
            <>
              {/* Диаметр хомутов */}
              <FormControl fullWidth>
                <InputLabel>Диаметр хомутов</InputLabel>
                <Select
                  value={clampDiameter}
                  label="Диаметр хомутов"
                  onChange={(e) => setClampDiameter(e.target.value)}
                >
                  {availableDiameters.map((diameter) => (
                    <MenuItem key={diameter} value={diameter}>
                      ⌀{diameter} мм
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {fastenerData?.hasAxialDistance !== false && (
                <TextField
                  label="Межосевое расстояние (мм)"
                  type="number"
                  value={axialDistance}
                  onChange={(e) => setAxialDistance(e.target.value)}
                  fullWidth
                  inputProps={{ step: 1 }}
                  helperText={`Расстояние между центрами хомутов (мин: ${roundUpToNearest5(getMinAxialDistance(clampDiameter, fastenerData?.displayName || 'YKRS-01'))} мм)`}
                />
              )}

              {fastenerData?.overhangRange && (
                <>
                  <FormControlLabel
                    sx={{ m: 0, alignItems: 'center' }}
                    control={(
                      <Checkbox
                        size="small"
                        checked={standardOverhang}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setStandardOverhang(checked);
                          if (checked) setOverhang(fastenerData.overhangRange.standard ?? 150);
                        }}
                      />
                    )}
                    label="Стандартный размер"
                  />
                  <TextField
                    label="Вылет (мм)"
                    type="number"
                    value={standardOverhang ? (fastenerData.overhangRange.standard ?? 150) : overhang}
                    onChange={(e) => setOverhang(e.target.value)}
                    disabled={standardOverhang}
                    fullWidth
                    inputProps={{
                      step: 1,
                      min: fastenerData.overhangRange.min,
                      max: fastenerData.overhangRange.max,
                    }}
                    helperText={`От оси трубы до стены (${fastenerData.overhangRange.min}–${fastenerData.overhangRange.max} мм)`}
                  />
                </>
              )}
            </>
          )}

          {/* Информация о крепеже */}
          {fastenerData?.description && (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                border: '0.0625rem solid #e0e0e0',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {fastenerData.description}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Отмена
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary">
          Добавить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FastenerConfigDialog;
